import { useState, useCallback, useMemo } from 'react';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocket from '../../../Hooks/useSocket';
import { Resource, Action } from '../../../Constants/socket';
import useVisualHighlight from '../../../Hooks/useVisualHighlight';

/**
 * Custom hook to handle channel real-time socket events.
 *
 * Args:
 *     channels: Current channels list.
 *     setChannels: Function to update channels.
 *
 * Returns:
 *     Object containing highlightRow, getHighlightClass, and softRefreshKey.
 */
const useChannelSocketEvents = (channels, setChannels) => {
    const { socket } = useSocket();
    const [softRefreshKey, setSoftRefreshKey] = useState(0);
    const { highlightRow, getHighlightClass } = useVisualHighlight();

    // Memoize channel IDs for socket subscription
    const channelIds = useMemo(() => {
        return channels.map(channel => channel.id).filter(Boolean);
    }, [channels]);
    const channelCursors = useMemo(() => channels.filter(channel => channel.id).map(channel => ({ id: channel.id, fromTimestamp: channel.updated_at })), [channels]);

    // Subscribe to channel resource with specific IDs
    useSocketSubscription(Resource.CHANNEL, channelIds.length > 0 ? channelIds : null, false, null, channelCursors);

    const handleChannelCreated = useCallback((data) => {
        // Avoid skeleton flash: do a silent/soft refresh and briefly highlight the new row
        if (data?.id) highlightRow(data.id, 'CREATE');
        setSoftRefreshKey(prev => prev + 1);

        // Subscribe to new channel from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.CHANNEL, [data.id], data.created_at);
        }
    }, [highlightRow, socket]);

    const handleChannelUpdated = useCallback((data) => {
        // Update the specific channel in state without reloading
        setChannels(prevChannels =>
            prevChannels.map(channel =>
                channel.id === data.id ? { ...channel, ...data } : channel
            )
        );
        if (data?.id) highlightRow(data.id, 'UPDATE');
    }, [setChannels, highlightRow]);

    const handleChannelDeleted = useCallback((data) => {
        // Remove the deleted channel from state
        setChannels(prevChannels =>
            prevChannels.filter(channel => channel.id !== data.id)
        );
    }, [setChannels]);

    // Register socket event listeners
    useSocketEvent(Resource.CHANNEL, Action.CREATE, handleChannelCreated);
    useSocketEvent(Resource.CHANNEL, Action.UPDATE, handleChannelUpdated);
    useSocketEvent(Resource.CHANNEL, Action.DELETE, handleChannelDeleted);

    return {
        getHighlightClass,
        softRefreshKey,
    };
};

export default useChannelSocketEvents;

